import {
  Accordion,
  Button,
  Divider,
  Flex,
  HelpHover,
  KitIcon,
  Menu,
  PlusIcon,
  Text,
  TextField,
  TrashIcon,
} from 'components'
import { kitIcons } from 'components/KitIcon'
import { MediaInput } from 'components/media'
import { startCase } from 'lodash-es'
import { TraitValue, TraitVariant } from 'types'
import { usePermission } from 'utils'

interface Props {
  value: TraitValue
  onChange: (value: TraitValue) => void
}

export default function ManageTraitValue({ value, onChange }: Props) {
  const { variants, ...baseProps } = value || {}

  function update(body: Partial<TraitValue>) {
    onChange({ ...value, ...body })
  }

  function updateVariant(index: number, body?: Partial<TraitVariant>) {
    const newVariants = [...(variants || [])]
    if (!body) newVariants.splice(index, 1)
    else newVariants[index] = { ...newVariants[index], ...body }

    update({ variants: newVariants })
  }

  return (
    <Flex direction="column">
      <TraitFields requiredFields={['title']} {...baseProps} onUpdate={update} />

      <Divider my={4} />

      <Flex gap={5} alignItems="center">
        <Text textStyle="h6">Variant Overrides</Text>
        <HelpHover text="Overrides are used to customize the trait for a specific audience. For example, you can use overrides to change the title and description of a trait for a specific region." />
      </Flex>

      {!!variants?.length && (
        <Accordion
          defaultValue={variants ? [variants.length.toString()] : undefined}
          items={variants?.map((variant, index) => ({
            id: variant.variantTitle,
            title: (
              <Flex flex={1} mr={8} gap={5} alignItems="center">
                <TextField
                  variant="flushed"
                  required
                  autoFocus={!variant.variantTitle}
                  value={variant.variantTitle ?? ''}
                  onChange={e => updateVariant(index, { variantTitle: e.target.value })}
                />
                <TrashIcon
                  size="sm"
                  onClick={e => {
                    e.stopPropagation()
                    updateVariant(index, undefined)
                  }}
                />
              </Flex>
            ),
            content: <TraitFields {...variant} onUpdate={value => updateVariant(index, value)} />,
          }))}
        />
      )}
      <Button
        variant="outline"
        mt={4}
        disabled={variants?.some(v => !v.variantTitle)}
        alignSelf="flex-start"
        leftIcon={<PlusIcon />}
        onClick={() => update({ variants: [...(variants || []), { variantTitle: '' }] })}
      >
        New Variant
      </Button>
    </Flex>
  )
}

interface TraitFieldsProps extends Partial<TraitValue> {
  onUpdate: (body: Partial<TraitValue>) => void
  requiredFields?: Array<'title' | 'subtitle' | 'description'>
}

function TraitFields({ onUpdate, requiredFields, ...props }: TraitFieldsProps) {
  const permission = usePermission()
  const isViewer = permission.content === 'viewer'
  const { title, subtitle, description, icon, media } = props

  const missingFields = [
    ...(!title ? ['title'] : []),
    ...(!subtitle ? ['subtitle'] : []),
    ...(!description ? ['description'] : []),
    ...(!icon ? ['icon'] : []),
    ...(!media ? ['media'] : []),
  ]

  return (
    <Flex gap={5} direction={'column'} mt={3} height={'95%'}>
      {title !== undefined && (
        <TextField
          label="Title"
          required={requiredFields?.includes('title')}
          disabled={isViewer}
          onChange={e => onUpdate({ title: { en: e.target.value } })}
          placeholder="Enter title"
          value={title?.en ?? ''}
        />
      )}

      {subtitle !== undefined && (
        <TextField
          label="Subtitle"
          required={requiredFields?.includes('subtitle')}
          disabled={isViewer}
          onChange={e => onUpdate({ subtitle: { en: e.target.value } })}
          placeholder="Enter subtitle"
          value={subtitle?.en ?? ''}
          isArea
        />
      )}

      {description !== undefined && (
        <TextField
          label="Description"
          required={requiredFields?.includes('description')}
          disabled={isViewer}
          onChange={e => onUpdate({ description: { en: e.target.value } })}
          placeholder="Enter description"
          value={description?.en ?? ''}
          isArea
        />
      )}

      {icon !== undefined && (
        <Menu
          contentProps={{ maxHeight: '300px', overflowY: 'auto' }}
          label="Icon"
          disabled={isViewer}
          alignSelf="flex-start"
          variant="outline"
          onChange={icon => onUpdate({ icon })}
          value={icon}
          options={kitIcons.map(icon => ({
            value: icon,
            label: (
              <Flex gap={2} alignItems="center">
                <KitIcon type={icon} size={24} />
                <Text>{startCase(icon)}</Text>
              </Flex>
            ),
          }))}
        />
      )}

      {media !== undefined && (
        <MediaInput
          label="Image"
          type="image"
          id={media}
          onSelect={media => onUpdate({ media })}
          onClear={media ? () => onUpdate({ media: null }) : undefined}
        />
      )}

      <Menu
        alignSelf="flex-start"
        variant="outline"
        label="New Field"
        options={missingFields.map(field => ({ value: field }))}
        hideCheckbox
        onChange={v => onUpdate({ [v]: null })}
      />
    </Flex>
  )
}
